/**
 * ============================================================================
 * EXECUTIVE SUMMARY TAB — renders N MetricTableSections from config.
 * ============================================================================
 *
 *
 * This is the content for tabs whose type = 'table-sections' in the view
 * config JSON. Each section is an independent MetricTableSection with its own
 * Period, search, and pagination — exactly as the Figma shows multiple
 * network summary tables stacked per network page.
 *
 * FROM THE FIGMA (scrolling down within a network's Executive Summary tab):
 *   ▾ Nova Network Summary                    (section 1)
 *   ▾ Nova Network Summary - On Time Delivery %   (section 2)
 *   ▾ (possibly more sections per config)
 *
 * EACH SECTION:
 * - Gets its config from the JSON (title, owner, goal, metrics list).
 * - Renders independently — the whole point of per-section local state.
 * - Is collapsible: users in the WBR can focus on just the section they're
 *   discussing.
 */

import React, { Suspense } from 'react';
import { lazyWithRetry } from 'src/helpers/lazyWithRetry';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
/**
 * LAZY + PRELOAD: MetricTableSection pulls in ag-grid, the heaviest static
 * dependency left in the initial bundle. Splitting it (together with the
 * already-lazy Deep Dive tab, the other ag-grid consumer) lets webpack move
 * ag-grid out of main.js entirely — nothing statically reaches it any more.
 *
 * webpackPreload (not Prefetch): this table IS the current navigation's content,
 * so the chunk must download in parallel with main.js at high priority rather
 * than waiting for idle. It resolves long before data does — the metrics window
 * takes seconds, measured — so this does not delay the paint.
 */
const MetricTableSection = lazyWithRetry(() =>
  import(
    /* webpackPreload: true */ 'src/components/table/MetricTableSection'
  ).then((module) => ({ default: module.MetricTableSection })),
);
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';
import { MetricTableSectionConfig } from 'src/types';
import { useAppSelector } from 'src/app/store';

/**
 * Suspense fallback for the lazily-loaded table chunk. Deliberately mirrors the
 * pre-JS shell's skeleton rows (public/index.html) so the visual state is
 * continuous from first paint through to real data, and so the vertical space
 * stays reserved (no layout shift when the table mounts).
 */
const TableSectionsSkeleton: React.FC = () => (
  <div aria-busy="true" aria-live="polite">
    <span className="shell-sr-only">Loading metrics table</span>
    {/* Mirrors the shell's .shell-main exactly (title + head + 8 rows) so the
        HTML shell -> fallback -> table transition reserves constant height. */}
    <div className="shell-title shell-row" aria-hidden="true" />
    {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
      <div
        key={row}
        className={row === 0 ? 'shell-row is-head' : 'shell-row'}
        aria-hidden="true"
      />
    ))}
  </div>
);

interface ExecutiveSummaryTabProps {
  sections?: MetricTableSectionConfig[];
}

/**
 * Which metric kinds each configured section shows. The view config's metric
 * name lists are honoured when they match the API's metric names; the type
 * split keeps the OTD-% section distinct. The summary section admits
 * percentage rows too (Committed vs Demand %); its name list decides which.
 */
const SECTION_METRIC_TYPES: Record<
  string,
  Array<'percentage' | 'volume' | 'count'>
> = {
  // Note: MetricTableSection's no-match fallback (stale name list) shows every
  // row of these types, so for summary that now includes the OTD percentages.
  summary: ['volume', 'count', 'percentage'],
  // Four catalog drop-off % rows plus the client-derived headline row.
  'pipeline-bottleneck': ['percentage'],
  otd: ['percentage'],
};

/**
 * Sections whose rows carry their own catalog owner/goal in the Metric cell.
 * Pipeline Bottleneck has three owners across four stage rows, so it must not
 * fall back to the section header's single owner.
 */
export const OWNER_COLUMN_SECTIONS: ReadonlySet<string> = new Set([
  'summary',
  'pipeline-bottleneck',
  'otd',
  'dsl-coverage',
]);

export const ExecutiveSummaryTab: React.FC<ExecutiveSummaryTabProps> = ({
  sections = [],
}) => {
  // Live/Snapshot comes from the page-level toggle: snapshot re-queries the
  // frozen WBR capture and enables the callout column (the snapshot is the
  // version discussed in the WBR meeting).
  const dataMode = useAppSelector((state) => state.dashboard.dataMode);
  const isSnapshot = dataMode === 'snapshot';

  // R10: the QuickSight-parity checkbox row decides which networks' section
  // groups render, stacked in canonical order. Toggling recomputes from the
  // cached rowset — no refetch.
  const selectedDemandCategories = useAppSelector(
    (state) => state.dashboard.selectedDemandCategories,
  );
  const showAllNetworks = useAppSelector(
    (state) => state.dashboard.showAllNetworks,
  );
  const visibleDemandCategories = DEMAND_CATEGORY_IDS.filter((id) =>
    selectedDemandCategories.includes(id),
  );

  // The All Networks group is the same three sections at the `all` scope —
  // no demand-category filter, so every table aggregates across every network
  // (spec all-networks-section-group R1/R2). It leads the stack, where the
  // old single-table OTD rollup used to render.
  const visibleNetworkScopes: string[] = [
    ...(showAllNetworks ? ['all'] : []),
    ...visibleDemandCategories,
  ];

  return (
    /* Fallback renders the same skeleton rows as the pre-JS shell in
       public/index.html, so a slow chunk degrades into "still loading" rather
       than a blank gap that would move LCP later and re-introduce layout shift. */
    <Suspense fallback={<TableSectionsSkeleton />}>
      <SpaceBetween direction="vertical" size="l">
      {/* Checkbox row renders at PAGE level above the filters (QuickSight
          order) — see ExecutiveSummaryPage. This tab only reads the state. */}
      {visibleNetworkScopes.map((networkId) =>
        sections.map((section) => {
          // The DCR Health section (dsl-coverage) renders in BOTH modes: its
          // L1 Quality Score + Launch TAT rows are live-capable. Only the
          // derived DSL Coverage rows are snapshot-only (collection_owner is
          // frozen at capture time — spec dsl-coverage-snapshot R1); the
          // section itself drops them in live mode (MetricTableSection).
          return (
            <MetricTableSection
              key={`${networkId}-${section.id}`}
              config={section}
              metricTypes={SECTION_METRIC_TYPES[section.id]}
              isSnapshot={isSnapshot}
              network={networkId}
              // DSL Coverage rows derive from collectionOwner, not the catalog.
              rowSource={
                section.id === 'dsl-coverage' ? 'dslCoverage' : 'catalog'
              }
              // Per-row Owner/Goal in the Metric cell for every configured
              // section — each metric carries its own catalog owner/goal.
              // Rows without either (the derived %DCRs rows) show neither.
              showOwnerColumns={OWNER_COLUMN_SECTIONS.has(section.id)}
            />
          );
        }),
      )}
      </SpaceBetween>
    </Suspense>
  );
};
